import React, { useEffect, useState } from 'react';
import '../App.css';



const HowItWorks = () => {

    const [lightboxImage, setLightboxImage] = useState(null);
    const [loadingFiles, setLoadingFiles] = useState({});

    const handleDownload = (e, url, fileName, key) => {
        e.preventDefault();
        setLoadingFiles(prev => ({ ...prev, [key]: true }));

        setTimeout(() => {
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                setLoadingFiles(prev => ({ ...prev, [key]: false }));
            }, 3000);
        }, 1500);
    };

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setLightboxImage(null);
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);
    useEffect(() => {
        let isManualScroll = false;
        const navLinks = document.querySelectorAll('.scrollspy-nav a');
        const sections = document.querySelectorAll('section[data-section]');

        const updateActive = (id) => {
            navLinks.forEach(link => {
                link.classList.toggle('active', link.getAttribute('href') === `#${id}`);
            });
        };

        const observer = new IntersectionObserver((entries) => {
            if (isManualScroll) return;

            const intersecting = entries.filter(e => e.isIntersecting);
            if (intersecting.length > 0) {
                const latest = intersecting[intersecting.length - 1];
                updateActive(latest.target.id);
            }
        }, {
            rootMargin: '-15% 0px -75% 0px',
            threshold: [0, 0.1]
        });

        sections.forEach(sec => observer.observe(sec));

        const handleHash = () => {
            const hash = window.location.hash;
            if (hash && hash !== '#') {
                const element = document.querySelector(hash);
                if (element) {
                    isManualScroll = true;
                    updateActive(hash.replace('#', ''));
                    element.scrollIntoView({ behavior: 'smooth' });

                    setTimeout(() => { isManualScroll = false; }, 1000);
                }
            }
        };

        handleHash();
        window.addEventListener('hashchange', handleHash);

        return () => {
            observer.disconnect();
            window.removeEventListener('hashchange', handleHash);
        };
    }, []);
    return (
        <>
            {lightboxImage && (
                <div
                    className="lightbox-backdrop"
                    onClick={() => setLightboxImage(null)}
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.95)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        cursor: 'zoom-out',
                        padding: '2rem',
                    }}
                >
                    <img
                        src={lightboxImage}
                        alt="Fullscreen"
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent close on image click
                    />
                    <button
                        onClick={() => setLightboxImage(null)}
                        style={{
                            position: 'absolute',
                            top: '2rem',
                            right: '2rem',
                            background: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '45px',
                            height: '45px',
                            fontSize: '1.2rem',
                            color: '#0d9488',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            transition: 'all 0.2s',
                            padding: 0
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.background = '#f0fdfa';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.background = 'white';
                        }}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            )}
            <section className="how-it-works-hero py-5">
                <div className="container text-center">
                    <h1 className="display-4 fw-bold mb-4 text-teal">
                        How Shopy Sync Works
                    </h1>
                    <p className="lead text-muted mb-0" style={{ maxWidth: '800px', margin: '0 auto' }}>
                        Take a moment to explore this guide — it’ll give you a solid understanding of how to <strong>unlock the full power</strong> of <strong>Shopy Sync</strong> and manage your Shopify products like a pro.
                    </p>
                </div>
            </section>


            <section className="py-5">
                <div className="container">
                    <div className="step-flow-container">

                        <div className="step-item">
                            <div className="step-circle bg-teal text-white">1</div>
                            <h5 className="mt-3 fw-bold">Export to GoogleSheet</h5>
                            <p className="text-muted small text-center">
                                Export all products with <strong>variants, inventory, price & media</strong> in a single <strong>Google Sheets</strong>.
                            </p>
                        </div>

                        <div className="step-arrow">
                            <i className="fas fa-arrow-right text-teal"></i>
                        </div>

                        <div className="step-item">
                            <div className="step-circle bg-teal text-white">2</div>
                            <h5 className="mt-3 fw-bold">Edit & Sync</h5>
                            <p className="text-muted small text-center">
                                Edit in sheet. Sync <strong>instantly</strong> (Auto Sync: ON) or <strong>in bulk</strong> (Auto Sync: OFF).
                            </p>
                        </div>

                        <div className="step-arrow">
                            <i className="fas fa-arrow-right text-teal"></i>
                        </div>

                        <div className="step-item">
                            <div className="step-circle bg-teal text-white">3</div>
                            <h5 className="mt-3 fw-bold">Bulk Update</h5>
                            <p className="text-muted small text-center">
                                Update <strong>variants, inventory, pricing & media</strong> for thousands of products in one upload.
                            </p>
                        </div>

                        <div className="step-arrow">
                            <i className="fas fa-arrow-right text-teal"></i>
                        </div>

                        <div className="step-item">
                            <div className="step-circle bg-teal text-white">4</div>
                            <h5 className="mt-3 fw-bold">Bulk Create</h5>
                            <p className="text-muted small text-center">
                                Add <strong>new products with multiple variants, images & inventory</strong> directly from <strong>Google Sheets</strong>.
                            </p>
                        </div>

                        <div className="step-arrow">
                            <i className="fas fa-arrow-right text-teal"></i>
                        </div>

                        <div className="step-item">
                            <div className="step-circle bg-teal text-white">5</div>
                            <h5 className="mt-3 fw-bold">Review & Repeat</h5>
                            <p className="text-muted small text-center">
                                Check results in the app. <br />
                                <strong>Repeat anytime</strong> for ongoing bulk updates.
                            </p>
                        </div>

                    </div>
                </div>
            </section>

            <section className="py-5 text-center">
                <div className="container">
                    <a
                        href="https://apps.shopify.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-teal-cta"
                    >
                        Get Shopy Sync now
                    </a>
                </div>
            </section>

            <section className="template-banner py-5">
                <div className="container px-4 px-lg-5">
                    <div className="row align-items-center g-5">

                        <div className="col-lg-6 text-white text-center text-lg-start">
                            <h2 className="display-5 fw-bold mb-3">
                                Shopy Sync Template for Shopify Product Sync
                            </h2>
                        </div>

                        <div className="col-lg-6 text-center">
                            <div className="template-screenshot shadow-lg">
                                <img
                                    src="/images/spreadsheet-sync-templatev3.png"
                                    alt="Shopy Sync Google Sheet with named tabs"
                                    className="img-fluid rounded"
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setLightboxImage('/images/spreadsheet-sync-templatev3.png')}
                                />
                            </div>
                        </div>

                    </div>
                </div>
            </section>
            <section className="py-5">
                <div className="container">
                    <div className="row">


                        <div className="col-lg-3 d-none d-lg-block">
                            <div className="scrollspy-nav sticky-top">
                                <ul className="nav flex-column">
                                    {[
                                        { id: 'welcome', label: 'Welcome Page' },
                                        { id: 'export', label: 'Export from Shopify' },
                                        { id: 'sheet-interface', label: 'Sheet Interface Guide' },
                                        { id: 'column-guide', label: 'Sheet Column Guide' },
                                        { id: 'bulk-add', label: 'Bulk Add (Sheet → Shopify)' },
                                        { id: 'bulk-update', label: 'Bulk Update (Sheet → Shopify)' },
                                        { id: 'bulk-delete-shopify', label: 'Bulk Delete (Shopify → Sheet)' },
                                        { id: 'bulk-delete-sheet', label: 'Bulk Delete (Sheet → Shopify)' },
                                        { id: 'all-jobs', label: 'All Jobs' },
                                        { id: 'settings', label: 'Settings' },
                                        { id: 'queued-jobs', label: 'Queued Jobs' },
                                    ].map((item) => (
                                        <li className="nav-item" key={item.id}>
                                            <a
                                                href={`#${item.id}`}
                                                className="nav-link text-muted py-2"
                                            >
                                                {item.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="col-lg-9">

                            <section id="welcome" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Welcome Page</h3>

                                <p className="mb-3">
                                    After installing Shopy Sync, the merchant lands on a Welcome page.
                                    Until they click <strong>Get Started</strong>, no app functionality is accessible.
                                </p>

                                <div className="mb-5">
                                    <p className="mb-3" >
                                        Clicking <strong>Get Started</strong>
                                    </p>

                                    <ol className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-3">
                                            <strong>1. Approval recorded:</strong> The app saves <code>status: "approved"</code> and
                                            <code> approved_at</code> timestamp in the database.
                                        </li>
                                        <li className="mb-3">
                                            <strong>2. All routes unlocked:</strong> The full app becomes accessible.
                                        </li>
                                        <li className="mb-3">
                                            <strong>3. Redirect:</strong> User is redirected to the app's home page.
                                        </li>
                                    </ol>
                                </div>

                                <div className="text-center mb-5">
                                    <img
                                        src="/images/welcome-get-startedv2.png"
                                        alt="Shopy Sync – Welcome / Get Started page"
                                        className="img-fluid rounded w-100"
                                        style={{
                                            maxHeight: '520px',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                            objectFit: 'contain',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => setLightboxImage('/images/welcome-get-startedv2.png')}
                                    />
                                    <p className="mt-3 text-muted text-center" style={{ fontSize: '1rem' }}>
                                        Welcome page shown after installation
                                    </p>
                                </div>

                                <hr className="my-5" style={{ borderTop: '2px solid #e9ecef' }} />
                            </section>

                            <section id="export" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Export from Shopify</h3>

                                <div className="mb-5">
                                    <h5 className="fw-semibold mb-3">1. Connect Your Google Account</h5>
                                    <p className="mb-3">
                                        First, connect your Google account to allow the app to create and manage your sync sheet.
                                    </p>

                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/connectGoogleAccv2.png"
                                            alt="Connect Google Account"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/connectGoogleAccv2.png')}
                                        />
                                    </div>
                                </div>

                                <div className="mb-5">
                                    <h5 className="fw-semibold mb-3">2. Sync Products to Google Sheet</h5>
                                    <p className="mb-3">
                                        <strong>Wait!</strong> Before clicking <strong>“Sync Products Now”</strong>, please check if your <strong>Google Apps Script API</strong> is enabled. If it is disabled, follow the steps below to enable it first, and then click the sync button to export all products, variants, inventory, prices, and images from Shopify.
                                    </p>


                                    <div className="prerequisite-notice">
                                        <div className="notice-header">
                                            <div className="notice-icon">
                                                <i className="fas fa-key"></i>
                                            </div>
                                            <div className="notice-title">
                                                <h4>Important Prerequisite for Two-Way Sync</h4>
                                            </div>
                                        </div>

                                        <p className="notice-description">
                                            Before clicking <strong>“Sync Products Now”</strong>, you must enable the <strong>Google Apps Script API</strong>.
                                            This allows the app to detect your sheet edits and sync them to Shopify.
                                        </p>

                                        <div className="notice-steps">
                                            <div className="step-item-new">
                                                <div className="step-label">
                                                    <span className="step-number">01</span>
                                                    <i className="fas fa-external-link-alt text-muted small"></i>
                                                </div>
                                                <p>Open <strong>Google Apps Script</strong> settings page in a new tab.</p>
                                                <a
                                                    href="https://script.google.com/home/usersettings"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-visit"
                                                >
                                                    Open Settings
                                                </a>
                                            </div>

                                            <div className="step-item-new">
                                                <div className="step-label">
                                                    <span className="step-number">02</span>
                                                    <i className="fas fa-search text-muted small"></i>
                                                </div>
                                                <p>Look for the <strong>Google Apps Script API</strong> toggle on that page.</p>
                                            </div>

                                            <div className="step-item-new">
                                                <div className="step-label">
                                                    <span className="step-number">03</span>
                                                    <i className="fas fa-toggle-on text-muted small"></i>
                                                </div>
                                                <p>If it is <strong>Off</strong>, toggle it to <strong>On</strong>. If already On, no action needed.</p>
                                            </div>
                                        </div>

                                        <div className="notice-footer">
                                            <i className="fas fa-shield-check"></i>
                                            <span>This is a one-time, secure setting per Google account. Changes may take 1-2 minutes to apply.</span>
                                        </div>
                                    </div>

                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/SyncProductv2.png"
                                            alt="Sync Products Now button"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/SyncProductv2.png')}
                                        />
                                    </div>

                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/syncprocessstep.png"
                                            alt="Sync Products Processing button"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/syncprocessstep.png')}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <h5 className="fw-semibold mb-3">Monitor Progress & View Sheet</h5>
                                        <p>
                                            The sync progress tracks the following stages in real-time:
                                        </p>

                                        <div className="sync-steps mb-4 p-3 border rounded bg-white shadow-sm">
                                            <div className="d-flex align-items-center mb-2 text-muted">
                                                <i className="fas fa-check-circle text-success me-2"></i>
                                                <span>Initializing Google Spreadsheet</span>
                                            </div>
                                            <div className="d-flex align-items-center mb-2 text-muted">
                                                <i className="fas fa-check-circle text-success me-2"></i>
                                                <span>Injecting Automation Script</span>
                                            </div>
                                            <div className="d-flex align-items-center mb-2 text-muted">
                                                <i className="fas fa-check-circle text-success me-2"></i>
                                                <span>Deploying Script</span>
                                            </div>
                                            <div className="d-flex align-items-center mb-2 text-muted">
                                                <i className="fas fa-check-circle text-success me-2"></i>
                                                <span>Fetching Products from Shopify</span>
                                            </div>
                                            <div className="d-flex align-items-center text-dark fw-bold">
                                                <i className="fas fa-circle-notch fa-spin text-teal me-2"></i>
                                                <span>Processing Batch Product Sync (Syncs 20 items per batch)</span>
                                            </div>
                                        </div>

                                        <div className="alert alert-warning border-0 bg-light text-dark">
                                            <div className="d-flex">
                                                <i className="fas fa-exclamation-circle mt-1 me-2 text-warning"></i>
                                                <div>
                                                    <strong>Important:</strong> The <strong>“View Spreadsheet”</strong> button will be enabled <strong>only after</strong> all products are fully synced. Use <strong>Job History</strong> to track batch progress.
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/SyncProductCompletedv2.png"
                                            alt="Sync Products Completed button"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/SyncProductCompletedv2.png')}
                                        />
                                    </div>

                                </div>

                                <div className="mb-5 p-4 border rounded bg-light">
                                    <h5 className="fw-semibold mb-3">3. Set Up Two-Way Sync (Mandatory)</h5>

                                    <p className="mb-4">
                                        After the sheet is created and fully synced from Shopify, <strong>open the Google Sheet</strong> and complete the following setup.
                                        This is required <strong>only once</strong> to enable automatic two-way sync (Sheet → Shopify).
                                    </p>

                                    <div className="row g-4">
                                        <div className="col-12">
                                            <div className="border rounded p-4 bg-white">
                                                <h6 className="fw-bold mb-3">
                                                    Step A: Authorize App (Required for onEdit & onChange Triggers)
                                                </h6>

                                                <ol className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                                    <li className="mb-3">
                                                        <strong>Open the Google Sheet</strong> → Go to <strong>Script</strong> menu
                                                    </li>
                                                    <li className="mb-3">
                                                        In the Script menu, click <strong>“Authorize App”</strong>
                                                        <div className="text-center my-3">
                                                            <img
                                                                src="/images/authorize-app-menu-newv3.png"
                                                                alt="Script → Authorize App"
                                                                className="img-fluid rounded w-100"
                                                                style={{
                                                                    maxHeight: '520px',
                                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                                    objectFit: 'contain',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => setLightboxImage('/images/authorize-app-menu-newv3.png')}
                                                            />
                                                            <p className="mt-2 small text-muted">Click “Authorize App” in the menu</p>
                                                        </div>
                                                    </li>
                                                    <li className="mb-3">
                                                        A popup “Authorization required” will appear → Click <strong>OK</strong>
                                                        <div className="text-center my-3">
                                                            <img
                                                                src="/images/authorization-required-popupv3.png"
                                                                alt="Authorization required – Click OK"
                                                                className="img-fluid rounded w-100"
                                                                style={{
                                                                    maxHeight: '520px',
                                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                                    objectFit: 'contain',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => setLightboxImage('/images/authorization-required-popupv3.png')}
                                                            />
                                                        </div>
                                                    </li>
                                                    <li className="mb-3">
                                                        Google sign-in & permissions screen appears → <strong>Select your Google account</strong> → Click <strong>Allow</strong> on all permissions
                                                        <div className="text-center my-3">
                                                            <img
                                                                src="/images/google-permissions-screenv3.png"
                                                                alt="Google Permissions – Click Allow on every permission"
                                                                className="img-fluid rounded w-100"
                                                                style={{
                                                                    maxHeight: '520px',
                                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                                    objectFit: 'contain',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => setLightboxImage('/images/google-permissions-screenv3.png')}
                                                            />
                                                            <p className="mt-2 small text-muted">
                                                                <strong>Important:</strong> You must click “Allow” on <u>every</u> permission (Drive, Sheets, Apps Script, etc.)
                                                            </p>
                                                        </div>
                                                    </li>
                                                </ol>

                                                <div className="p-3 rounded">
                                                    <p className="mb-0">
                                                        <strong>What this does:</strong><br />
                                                        <p>
                                                            • Grants the app full edit access to the sheet<br />
                                                            • Automatically installs <code>onEdit()</code> and <code>onChange()</code> triggers<br />
                                                            • Enables detection of edits, adds, and deletes in the sheet → sync to Shopify (instantly or in bulk)

                                                        </p>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 p-4 border rounded bg-white">
                                        <h6 className="fw-bold mb-3">
                                            Step B: Initialize Snapshot (Run ONLY after full export is complete)
                                        </h6>

                                        <ol className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                            <li className="mb-3">
                                                In the same <strong>Script</strong> menu, click <strong>“Initialize Snapshot”</strong>
                                                <div className="text-center my-3">
                                                    <img
                                                        src="/images/initialize-snapshot-menuv3.png"
                                                        alt="Script menu → Click Initialize Snapshot"
                                                        className="img-fluid rounded w-100"
                                                        style={{
                                                            maxHeight: '520px',
                                                            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                            objectFit: 'contain',
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() => setLightboxImage('/images/initialize-snapshot-menuv3.png')}
                                                    />
                                                    <p className="mt-2 small text-muted">Click “Initialize Snapshot”</p>
                                                </div>
                                            </li>
                                        </ol>

                                        <div className="p-3 rounded">
                                            <p className="mb-0">
                                                <strong>What this does:</strong><br />
                                                <p>
                                                    • Takes a complete snapshot of current sheet data<br />
                                                    • Enables detection of future adds, updates, and deletes<br />
                                                    • Required <strong>once</strong> after full Shopify export
                                                </p>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="premium-alert mt-4">
                                        <div className="premium-alert-icon">
                                            <i className="fas fa-exclamation-triangle"></i>
                                        </div>
                                        <div className="premium-alert-content">
                                            <div className="premium-alert-title">Critical Order & Requirements:</div>
                                            <div className="premium-alert-body">
                                                <ul className="mb-0">
                                                    <li>
                                                        <strong>Authorize App</strong> → Can be done <u>anytime</u>, but must be completed <u>before Initialize Snapshot</u>
                                                    </li>
                                                    <li><strong>Initialize Snapshot</strong> → Run <u>only after</u> the full export from Shopify is 100% complete</li>
                                                    <li>Both steps are <u>mandatory</u> for Sheet → Shopify sync</li>
                                                    <li>You only need to do this <u>once per sheet</u></li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr className="my-5" />

                                <p className="fw-semibold">
                                    Done! Your Shopify store is now fully synced with Google Sheets.
                                    Edit the sheet → changes sync back to Shopify based on your <strong>Sync Menu</strong> settings.
                                </p>
                            </section>

                            <section id="sheet-interface" data-section className="sync-control-container mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Sync Controls & Menu</h3>
                                <p className="mb-4">
                                    From the <strong>Sync</strong> menu in your Google Sheet, you can control how and when your changes are pushed to Shopify.
                                </p>


                                <img
                                    src="/images/sync-menu-optionsv3.png"
                                    alt="Google Sheets Sync Menu"
                                    className="img-fluid rounded shadow-sm clickable-image"
                                    style={{ maxHeight: '520px' }}
                                    onClick={() => setLightboxImage('/images/sync-menu-optionsv3.png')}
                                />
                                <p className="m-4 text-muted small text-center">The Sync menu allows toggling Auto Sync and running manual syncs.</p>


                                <div className="sync-modes">
                                    <div className="sync-mode-card">
                                        <span className="mode-badge mode-off">Mode: OFF</span>
                                        <h5>Manual Sync</h5>
                                        <p>
                                            When Auto Sync is <strong>OFF</strong>, you can make <strong>multiple changes</strong> (create, update, delete) without triggering Shopify updates immediately.
                                            Once finished, click <strong>“Run Sync Now”</strong> to apply all operations in a single batch.
                                        </p>
                                    </div>

                                    <div className="sync-mode-card">
                                        <span className="mode-badge mode-on">Mode: ON</span>
                                        <h5>Real-Time Sync</h5>
                                        <p>
                                            When Auto Sync is <strong>ON</strong>, <strong>any change to a cell</strong> is synced to Shopify <strong>instantly</strong>.
                                            This is perfect for quick individual updates or managing inventory on the fly.
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-4 p-3 bg-light rounded text-center">
                                    <p className="mb-0 small text-muted">
                                        <i className="fas fa-info-circle me-2 text-teal"></i>
                                        You can **toggle Auto Sync ON/OFF anytime** from the same Sync menu.
                                    </p>
                                </div>
                            </section>

                            <section id="column-guide" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Sheet Column Guide</h3>
                                <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                    Understanding the column headers in your Google Sheet is essential for a smooth sync. Each column name includes a label to guide you.
                                </p>

                                <div className="text-center mb-5">
                                    <img
                                        src="/images/sheet-header-guidev3.png"
                                        alt="Google Sheet Header Guide"
                                        className="img-fluid rounded w-100"
                                        style={{
                                            maxHeight: '520px',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                            objectFit: 'contain',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => setLightboxImage('/images/sheet-header-guidev3.png')}
                                    />
                                    <p className="mt-3 text-muted text-center" style={{ fontSize: '1rem' }}>
                                        Header labels in Google Sheet: (Auto), (Required), and (Optional)
                                    </p>
                                </div>

                                <div className="row g-4 mb-5">
                                    <div className="col-md-4">
                                        <div className="border rounded p-4 h-100 bg-white shadow-sm">
                                            <h6 className="fw-bold text-primary mb-3">
                                                <i className="fas fa-magic me-2"></i>(Auto) Fields
                                            </h6>
                                            <p className="small text-muted mb-0">
                                                These fields are managed <strong>automatically</strong> by the app. You don't need to add or edit data here (e.g., ID, Handle, Created At).
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="border rounded p-4 h-100 bg-white shadow-sm">
                                            <h6 className="fw-bold text-danger mb-3">
                                                <i className="fas fa-exclamation-circle me-2"></i>(Required) Fields
                                            </h6>
                                            <p className="small text-muted mb-0">
                                                These fields are <strong>mandatory</strong>. You must fill these columns (e.g., Title) for the product to be created or updated.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="border rounded p-4 h-100 bg-white shadow-sm">
                                            <h6 className="fw-bold text-success mb-3">
                                                <i className="fas fa-plus-circle me-2"></i>(Optional) Fields
                                            </h6>
                                            <p className="small text-muted mb-0">
                                                These fields are <strong>flexible</strong>. You can add data or leave them blank based on your requirements (e.g., Description, Tags).
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="premium-alert" style={{ background: '#f0f9ff', borderColor: '#bae6fd' }}>
                                    <div className="premium-alert-icon" style={{ background: '#0ea5e9' }}>
                                        <i className="fas fa-info-circle"></i>
                                    </div>
                                    <div className="premium-alert-content">
                                        <div className="premium-alert-title" style={{ color: '#0369a1' }}>Pro Tip: Adding New Variants to Existing Products</div>
                                        <div className="premium-alert-body" style={{ color: '#0c4a6e' }}>
                                            <p className="mb-0">
                                                When you want to add a <strong>new variant</strong> to an already existing product:
                                            </p>
                                            <ul className="mt-2 mb-0">
                                                <li><strong>Product ID:</strong> Must be filled with the existing product's ID.</li>
                                                <li><strong>Variant ID:</strong> Must be left <strong>Empty</strong>. Shopy Sync will generate a new one automatically.</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <hr className="my-5" />
                            </section>

                            <section id="bulk-add" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Bulk Add Products (Sheet → Shopify)</h3>

                                <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                    After completing <strong>Authorize App</strong> and <strong>Initialize Snapshot</strong>, you can add <strong>thousands of new products</strong> — including <strong>variants, inventory, pricing, images</strong> — directly from your Google Sheet to Shopify.
                                </p>

                                {/* How to Add New Products */}
                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>How to Add New Products</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Simply <strong>paste your product data</strong> into the Google Sheet. New rows will be detected and synced to Shopify (instantly or after clicking <strong>"Run Sync Now"</strong>.
                                    </p>


                                    <p className="mb-5" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        You can also create products manually! By turning <strong>Auto Sync OFF</strong>, you can write or edit product data directly in the Google Sheet without pasting, and then sync it to Shopify later by clicking <strong>"Run Sync Now"</strong>.
                                    </p>

                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/bulk-add-paste-datav3.png"
                                            alt="Paste product data starting from row 2"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/bulk-add-paste-datav3.png')}
                                        />
                                        <p className="mt-3 text-muted text-center" style={{ fontSize: '1rem' }}>
                                            Paste product data in Google Sheet
                                        </p>
                                    </div>
                                </div>

                                <div className="mb-5">

                                    <div className="mb-5">
                                        <h5 className="fw-bold mb-3" style={{ fontSize: '1.35rem', color: '#212529' }}>
                                            Multiple Variants
                                        </h5>
                                        <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                            Add up to <strong>3 variant option names</strong> (e.g., <code>Size</code>, <code>Color</code>, <code>Material</code>).<br />
                                            Use <strong>commas</strong> to separate multiple values in each option.
                                        </p>
                                        <div className="text-center">
                                            <img
                                                src="/images/variant-options-commav3.png"
                                                alt="Option Name and Value with comma separation"
                                                className="img-fluid rounded w-100"
                                                style={{
                                                    maxHeight: '520px',
                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                    objectFit: 'contain',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => setLightboxImage('/images/variant-options-commav3.png')}
                                            />
                                        </div>
                                        <p className="mt-3 text-muted text-center" style={{ fontSize: '1rem' }}>
                                            Option Values with commas
                                        </p>
                                    </div>

                                    <div className="mb-5">
                                        <h5 className="fw-bold mb-3" style={{ fontSize: '1.35rem', color: '#212529' }}>
                                            Media & Inventory
                                        </h5>
                                        <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                            Add <strong>multiple images</strong> or <strong>inventory quantities</strong> using <strong>comma-separated values</strong>.
                                        </p>
                                        <div className="bg-light p-4 rounded">
                                            <ul className="list-unstyled mb-0" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                                <li className="mb-3">
                                                    <strong>Media:</strong> <code className="bg-white px-2 py-1 rounded">https://img1.jpg, https://img2.jpg, https://img3.jpg</code>
                                                </li>
                                                <li className="mb-3">
                                                    <strong>Inventory Item:</strong> <code className="bg-white px-2 py-1 rounded">100, 50, 200</code>
                                                </li>
                                                <li className="mb-3">
                                                    <strong>Variant Media:</strong> <code className="bg-white px-2 py-1 rounded">https://variant1.jpg, https://variant2.jpg</code>
                                                </li>
                                                <li className="mb-3">
                                                    <strong>Location-Wise Inventory:</strong> Set stock quantities for specific locations using location columns.
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                </div>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>How the Sync Works</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Once you paste data and sync (Auto Sync ON or manual click):
                                    </p>
                                    <ol className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-3">
                                            <strong>1. Bulk Creation:</strong> Products are created in Shopify in <strong>batches of 200</strong> for maximum speed.
                                        </li>
                                        <li className="mb-3">
                                            <strong>2. Product ID Sync:</strong> As soon as a product is created, its <strong>Shopify Product ID</strong> is instantly written back to the sheet in the <code>ID</code> column.
                                        </li>

                                        <div className="text-center mb-5">
                                            <img
                                                src="/images/product-id-syncv3.png"
                                                alt="product-id-sync.png"
                                                className="img-fluid rounded w-100"
                                                style={{
                                                    maxHeight: '520px',
                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                    objectFit: 'contain',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => setLightboxImage('/images/product-id-syncv3.png')}
                                            />
                                        </div>

                                        <li className="mb-3">
                                            <strong>3. Variant Expansion:</strong> Each product is expanded into <strong>one row per variant</strong> in the sheet — with full details like <code>Variant Title</code>, <code>Price</code>, <code>Inventory</code>, and <code>Variant ID</code>.
                                        </li>
                                    </ol>

                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/after-sync-variant-rowsv3.png"
                                            alt="Each product expands to one row per variant after sync"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/after-sync-variant-rowsv3.png')}
                                        />
                                        <p className="mt-3 text-muted text-center" style={{ fontSize: '1rem' }}>
                                            After sync: 1 product with 4 variants = 4 rows in the sheet
                                        </p>
                                    </div>
                                </div>

                                <div className="row g-4 text-center py-4">

                                    <div className="col-lg-6">
                                        <p className="mb-3" style={{ fontSize: '1.15rem', color: '#212529' }}>
                                            Download the <strong>input template</strong> to see the exact format required when adding products.
                                        </p>
                                        <a
                                            href="https://docs.google.com/spreadsheets/d/1sR3WXsHQjV6Cp3rPdoA3WUunCmmy3Qdmt4Q9Moi9JaE/export?format=xlsx"
                                            onClick={(e) => handleDownload(e, 'https://docs.google.com/spreadsheets/d/1sR3WXsHQjV6Cp3rPdoA3WUunCmmy3Qdmt4Q9Moi9JaE/export?format=xlsx', 'Spreadsheet_Sync_Input_Template.xlsx', 'input_template')}
                                            className="btn btn-outline-teal btn-lg px-5 py-3 text-teal"
                                            style={{ fontSize: '1.2rem', fontWeight: '600', borderWidth: '2px', borderRadius: '12px' }}
                                        >
                                            {loadingFiles['input_template'] ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Downloading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-download me-2"></i>
                                                    Download Input Template
                                                </>
                                            )}
                                        </a>
                                    </div>

                                    <div className="col-lg-6">
                                        <p className="mb-3" style={{ fontSize: '1.15rem', color: '#212529' }}>
                                            Download the <strong>output example</strong> to see how data appears in the sheet <strong>after</strong> products are created in Shopify.
                                        </p>
                                        <a
                                            href="https://docs.google.com/spreadsheets/d/1fABMkNIeHiPoDweJbmutd_w9dEonz4VbE7SXXIer89Q/export?format=xlsx"
                                            onClick={(e) => handleDownload(e, 'https://docs.google.com/spreadsheets/d/1fABMkNIeHiPoDweJbmutd_w9dEonz4VbE7SXXIer89Q/export?format=xlsx', 'Spreadsheet_Sync_Output_Example.xlsx', 'output_template')}
                                            className="btn btn-outline-teal btn-lg px-5 py-3 text-teal"
                                            style={{ fontSize: '1.2rem', fontWeight: '600', borderWidth: '2px', borderRadius: '12px' }}
                                        >
                                            {loadingFiles['output_template'] ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Downloading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-download me-2"></i>
                                                    Download Output Template
                                                </>
                                            )}
                                        </a>
                                    </div>

                                </div>

                            </section>

                            <section id="bulk-update" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Bulk Update Products (Sheet → Shopify)</h3>

                                <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                    Update product data like <strong>titles, variant option names and values, prices, inventory, descriptions, images, videos, 3D models, and more</strong> for thousands of products — including <strong>all variants</strong> — directly from your Google Sheet.
                                </p>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>How to Update Products</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Simply <strong>edit any cell</strong> in the sheet. Changes are detected and synced to Shopify (instantly or in bulk, depending on your setting).
                                    </p>

                                    <div className="premium-alert mb-5" style={{ background: '#f0f9ff', borderColor: '#bae6fd' }}>
                                        <div className="premium-alert-icon" style={{ background: '#0ea5e9' }}>
                                            <i className="fas fa-info-circle"></i>
                                        </div>
                                        <div className="premium-alert-content">
                                            <div className="premium-alert-title" style={{ color: '#0369a1' }}>Important Rule: Product-Level Fields</div>
                                            <div className="premium-alert-body" style={{ color: '#0c4a6e' }}>
                                                For products with <strong>multiple variants</strong>, <strong>product-level fields</strong> (e.g., <code>Title</code>, <code>Description</code>, <code>Product Media</code>, <code>Tags</code>, <code>Category</code>) must be updated in the <strong>first row</strong> of that product group only.
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Update Any Field in the Sheet — All Product Data is Editable</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Every field in your Google Sheet can be updated directly. Simply edit the cell; changes sync to Shopify based on your Auto Sync preference.
                                    </p>

                                    <div className="row g-5">

                                        <div className="col-lg-6">
                                            <h6 className="fw-bold mb-3" style={{ fontSize: '1.25rem', color: '#212529' }}>Variant Data</h6>
                                            <p className="mb-3" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                                Update any variant-specific field in its own row:
                                            </p>
                                            <ul className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                                <li className="mb-2">• <code>Option1 Name/Value</code>, <code>Option2</code>, <code>Option3</code></li>
                                                <li className="mb-2">• <code>Variant Price</code>, <code>Compare at Price</code></li>
                                                <li className="mb-2">• <code>Variant SKU</code>, <code>Barcode</code></li>
                                                <li className="mb-2">• <code>Variant Weight</code>, <code>Weight Unit</code></li>
                                                <li className="mb-2">• <code>Variant Media</code>: <code>https://img1.jpg, https://img2.jpg</code> <span className="text-danger">(Images only)</span></li>
                                            </ul>
                                        </div>

                                        <div className="col-lg-6">
                                            <h6 className="fw-bold mb-3" style={{ fontSize: '1.25rem', color: '#212529' }}>Product & Inventory</h6>
                                            <p className="mb-3" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                                Update product-wide or inventory fields:
                                            </p>
                                            <ul className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                                <li className="mb-2">• <code>Title</code>, <code>Handle</code>, <code>Description</code>, <code>Tags</code></li>
                                                <li className="mb-2">• <code>Product Type</code>, <code>Vendor</code>, <code>Published Scope</code></li>
                                                <li className="mb-2">• <code>Location-Wise Inventory</code>: Update stock levels across all your store locations</li>
                                                <li className="mb-2">• <code>Product Media</code>: <code>https://img.jpg, https://video.mp4, https://model.glb</code> <span className="text-success">(Images, Videos, 3D Models)</span></li>
                                            </ul>
                                        </div>

                                    </div>
                                </div>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Media Support</h5>
                                    <div className="bg-light p-4 rounded">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p className="mb-2" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                                    <strong>Variant Media</strong> (per variant):
                                                </p>
                                                <p className="mb-0 small text-success">
                                                    Supported: <strong>Images only</strong> (JPG, JPEG, PNG, GIF)
                                                </p>
                                                <p className="mb-0 small text-danger">
                                                    Not Supported: Videos, 3D models
                                                </p>
                                            </div>
                                            <div className="col-md-6">
                                                <p className="mb-2" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                                    <strong>Product Media</strong> (main gallery):
                                                </p>
                                                <p className="mb-0 small text-success">
                                                    Supported: <strong>Images, Videos, 3D Models</strong>
                                                </p>
                                            </div>
                                        </div>
                                        <hr className="my-3" />
                                        <p className="mb-0" style={{ fontSize: '1rem', color: '#212529' }}>
                                            <strong>Note:</strong> All media links must be <strong>publicly accessible</strong> (no login, no permissions required).
                                        </p>
                                    </div>
                                </div>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Sync Behavior</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        After you edit the sheet:
                                    </p>
                                    <ul className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-3">
                                            <strong>Sheet → Shopify:</strong> Updated fields are pushed to Shopify.                                      </li>
                                        <li className="mb-3">
                                            <strong>Shopify → Sheet (Webhook Sync):</strong> Once Shopify processes the update, the <strong>latest data</strong> (including any auto-generated fields like <code>Variant ID</code>, <code>Updated At</code>) is <strong>pulled back</strong> into the sheet.
                                        </li>
                                    </ul>
                                    <p className="mt-5 text-center">
                                        <span className="badge p-3" style={{ fontSize: '1rem', backgroundColor: '#0d9488' }}>
                                            Two-way sync: Edit in sheet → Update in Shopify → Refresh in sheet
                                        </span>
                                    </p>

                                    <div className="premium-alert mt-5" style={{ background: '#fff0f0', borderColor: '#ffc9c9' }}>
                                        <div className="premium-alert-icon" style={{ background: '#ef4444' }}>
                                            <i className="fas fa-hand-paper"></i>
                                        </div>
                                        <div className="premium-alert-content">
                                            <div className="premium-alert-title" style={{ color: '#b91c1c' }}>Best Practice: Wait for Sheet Refresh</div>
                                            <div className="premium-alert-body" style={{ color: '#7f1d1d' }}>
                                                <p className="mb-2">
                                                    For the smoothest experience, <strong>please wait for the Google Sheet to fully update</strong> (via webhooks) before starting a new bulk create or update task.
                                                </p>
                                                <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                                                    <strong>Why?</strong> After a sync, Shopify sends data back to your sheet (creating rows, updating IDs). Editing the sheet while it's actively updating rows or columns can cause data conflicts.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-5 p-4 bg-light border rounded">
                                    <h5 className="fw-bold text-teal mb-3" style={{ fontSize: '1.35rem' }}>
                                        How to Bulk Update Multiple Products
                                    </h5>
                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/bulk-update-beforev3.png"
                                            alt="Before: Multiple product descriptions selected"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/bulk-update-beforev3.png')}
                                        />
                                    </div>
                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/bulk-update-afterv3.png"
                                            alt="After: Descriptions pasted and updated in Google Sheet"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/bulk-update-afterv3.png')}
                                        />
                                        <p className="mt-3 text-muted text-center" style={{ fontSize: '1rem' }}>
                                            To view the updated data, please check the <code>Update Input Example</code> and <code>Post-Update Example</code> sheets provided below.
                                        </p>
                                    </div>
                                </div>

                                <div className="mb-5 p-4 bg-light border rounded">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>
                                        Bulk Update: Variant Add, Update & Delete
                                    </h5>

                                    <div className="mb-4">
                                        <p className="mb-3" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                            Manage variant changes directly from the Google Sheet. You can <strong>add new variants</strong>, <strong>update existing option names/values</strong>, or <strong>delete specific variants</strong> by managing rows under the same product ID / handle.
                                        </p>
                                    </div>

                                    <div className="row g-4 mb-4">
                                        <div className="col-md-4">
                                            <div className="bg-white p-4 rounded border h-100 shadow-sm">
                                                <div className="text-teal mb-3" style={{ fontSize: '1.5rem' }}>
                                                    <i className="fas fa-plus-circle"></i>
                                                </div>
                                                <h6 className="fw-bold">Add New Variants</h6>
                                                <p className="small text-muted mb-0">
                                                    Insert new rows under the same product. Keep <strong>Variant ID blank</strong> for new variants.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="bg-white p-4 rounded border h-100 shadow-sm">
                                                <div className="text-primary mb-3" style={{ fontSize: '1.5rem' }}>
                                                    <i className="fas fa-edit"></i>
                                                </div>
                                                <h6 className="fw-bold">Update Options</h6>
                                                <p className="small text-muted mb-0">
                                                    Edit Option Names or Values. Keep the <strong>Variant ID</strong> to treat it as an update.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="bg-white p-4 rounded border h-100 shadow-sm">
                                                <div className="text-danger mb-3" style={{ fontSize: '1.5rem' }}>
                                                    <i className="fas fa-trash-alt"></i>
                                                </div>
                                                <h6 className="fw-bold">Delete Variants</h6>
                                                <p className="small text-muted mb-0">
                                                    Simply remove the variant row from the sheet to delete it from Shopify.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-5">
                                        <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Example: Variant Changes</h5>

                                        <div className="mb-5">
                                            <h6 className="fw-bold mb-2">Before: Original Variants</h6>
                                            <p className="text-muted small mb-3"><i>(Full image shown below)</i></p>
                                            <div className="img-container bg-white p-2 rounded border mb-4">
                                                <img
                                                    src="/images/before_update_variantv2.png"
                                                    alt="Before Variant Update"
                                                    className="img-fluid rounded clickable-image w-100"
                                                    style={{ cursor: 'pointer', objectFit: 'contain' }}
                                                    onClick={() => setLightboxImage('/images/before_update_variantv2.png')}
                                                />
                                            </div>

                                            <h6 className="fw-bold mb-2">After: Updated Options & Variant Changes</h6>
                                            <p className="text-muted small mb-3"><i>(Full image shown below)</i></p>
                                            <div className="img-container bg-white p-2 rounded border mb-5">
                                                <img
                                                    src="/images/after_update_variantv2.png"
                                                    alt="After Variant Update"
                                                    className="img-fluid rounded clickable-image w-100"
                                                    style={{ cursor: 'pointer', objectFit: 'contain' }}
                                                    onClick={() => setLightboxImage('/images/after_update_variantv2.png')}
                                                />
                                            </div>
                                        </div>

                                        <div className="bg-white p-4 rounded border shadow-sm">
                                            <h6 className="fw-bold mb-3" style={{ fontSize: '1.2rem' }}>What Changed in These Examples</h6>
                                            <p className="mb-4" style={{ fontSize: '1.05rem', color: '#4b5563' }}>
                                                As you can see in the images above, the Bulk Update feature allows you to easily manage variant changes directly from the sheet:
                                            </p>
                                            <ul className="list-unstyled mb-4" style={{ fontSize: '1.05rem', color: '#4b5563' }}>
                                                <li className="mb-3 d-flex align-items-start">
                                                    <i className="fas fa-check-circle text-teal me-2 mt-1"></i>
                                                    <span><strong>Existing variant option names and option values are updated</strong> (as shown in Product 3&4).</span>
                                                </li>
                                                <li className="mb-3 d-flex align-items-start">
                                                    <i className="fas fa-check-circle text-teal me-2 mt-1"></i>
                                                    <span><strong>New variants are added</strong> by inserting new rows under the same product (as shown in Product 1&2).</span>
                                                </li>
                                                <li className="mb-3 d-flex align-items-start">
                                                    <i className="fas fa-check-circle text-teal me-2 mt-1"></i>
                                                    <span><strong>Specific variants can be deleted</strong> by removing only that variant’s row (as shown in Product 5).</span>
                                                </li>
                                                <li className="mb-3 d-flex align-items-start">
                                                    <i className="fas fa-check-circle text-teal me-2 mt-1"></i>
                                                    <span>All other variants remain unchanged and continue to sync correctly.</span>
                                                </li>
                                            </ul>

                                            <p className="mt-4 fw-bold text-teal mb-0">
                                                This approach gives you full control over variant-level updates during bulk operations.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="p-4 rounded border bg-white mb-4">
                                        <h6 className="fw-bold mb-3"><i className="fas fa-sync-alt me-2 text-teal"></i>Update Variant-Level Details</h6>
                                        <p className="mb-0" style={{ fontSize: '1.05rem', color: '#4b5563' }}>
                                            Modify <strong>Price, SKU, Barcode, Inventory, and Images</strong> for any variant. All updates are processed together during the sync.
                                        </p>
                                    </div>

                                    <div className="premium-alert mt-4" style={{ background: '#fff7ed', borderColor: '#ffedd5' }}>
                                        <div className="premium-alert-icon" style={{ background: '#f97316' }}>
                                            <i className="fas fa-exclamation-triangle"></i>
                                        </div>
                                        <div className="premium-alert-content">
                                            <div className="premium-alert-title" style={{ color: '#9a3412' }}>Important Rules:</div>
                                            <div className="premium-alert-body" style={{ color: '#7c2d12' }}>
                                                <ul className="mb-0">
                                                    <li><strong>New variants</strong> → Variant ID must be <strong>blank</strong></li>
                                                    <li><strong>Existing variants</strong> → Variant ID must be <strong>present</strong></li>
                                                    <li>Option names and values are fully editable without creating new variants</li>
                                                    <li>Variant option combinations (e.g., Red/XL) must be unique</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-4 text-center py-4">

                                    <div className="col-lg-6">
                                        <p className="mb-3" style={{ fontSize: '1.15rem', color: '#212529' }}>
                                            Download the <strong>update template</strong> to see correct column structure.
                                        </p>
                                        <a
                                            href="https://docs.google.com/spreadsheets/d/180UTwbz4zSBSrs9hcZxOptOy8W4kVRsjBltXhGfYaXU/export?format=xlsx"
                                            onClick={(e) => handleDownload(e, 'https://docs.google.com/spreadsheets/d/180UTwbz4zSBSrs9hcZxOptOy8W4kVRsjBltXhGfYaXU/export?format=xlsx', 'Spreadsheet_Sync_Update_Template.xlsx', 'update_template')}
                                            className="btn btn-outline-teal btn-lg px-5 py-3 text-teal"
                                            style={{ fontSize: '1.2rem', fontWeight: '600', borderWidth: '2px', borderRadius: '12px' }}
                                        >
                                            {loadingFiles['update_template'] ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Downloading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-download me-2"></i>
                                                    Download Update Template
                                                </>
                                            )}
                                        </a>
                                    </div>

                                    <div className="col-lg-6">
                                        <p className="mb-3" style={{ fontSize: '1.15rem', color: '#212529' }}>
                                            Download the <strong>post-update example</strong> to see how data refreshes after sync.
                                        </p>
                                        <a
                                            href="https://docs.google.com/spreadsheets/d/1b8M2jBInhU907UlcpLaYfS5_6L2UuVQ00d8xWdsVmbo/export?format=xlsx"
                                            onClick={(e) => handleDownload(e, 'https://docs.google.com/spreadsheets/d/1b8M2jBInhU907UlcpLaYfS5_6L2UuVQ00d8xWdsVmbo/export?format=xlsx', 'Spreadsheet_Sync_Post_Update_Example.xlsx', 'post_update_example')}
                                            className="btn btn-outline-teal btn-lg px-5 py-3 text-teal"
                                            style={{ fontSize: '1.2rem', fontWeight: '600', borderWidth: '2px', borderRadius: '12px' }}
                                        >
                                            {loadingFiles['post_update_example'] ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Downloading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-download me-2"></i>
                                                    Download Post-Update Example
                                                </>
                                            )}
                                        </a>
                                    </div>

                                </div>
                            </section>

                            <section id="bulk-delete-shopify" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Bulk Delete (Shopify → Sheet)</h3>

                                <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                    When you delete products directly in <strong>Shopify</strong>, those same products are
                                    automatically <strong>removed from your Google Sheet</strong> — keeping your spreadsheet
                                    perfectly in sync with your live store data.
                                </p>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>How the Deletion Sync Works</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Whenever you bulk delete products from your Shopify admin (or through any app/API),
                                        <strong>Shopy Sync</strong> automatically detects those deletions using real-time
                                        <strong> Shopify Webhooks</strong> and removes the same rows from your connected Google Sheet.
                                    </p>

                                    <ol className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-3">
                                            <strong>1. Shopify Product Deletion:</strong> Products are deleted manually or in bulk from your Shopify admin or via any connected app.
                                        </li>
                                        <li className="mb-3">
                                            <strong>2. Webhook Detection:</strong> The app instantly receives a <code>product/delete</code> event from Shopify.
                                        </li>
                                        <li className="mb-3">
                                            <strong>3. Sheet Sync:</strong> Corresponding rows (matching deleted Product IDs) are automatically removed from your Google Sheet — within seconds.
                                        </li>
                                    </ol>

                                </div>

                                <div className="mb-5 bg-light p-4 rounded border">
                                    <h6 className="fw-bold mb-3" style={{ fontSize: '1.25rem', color: '#212529' }}>Key Notes</h6>
                                    <ul className="list-unstyled mb-0" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-3">• This works for <strong>single or bulk deletions</strong> done in Shopify.</li>
                                        <li className="mb-3">• No manual refresh required — the sheet auto-updates using <strong>real-time webhooks</strong>.</li>
                                        <li className="mb-3">• Keeps your sheet <strong>clean, accurate, and always in sync</strong> with your live store data.</li>
                                    </ul>
                                </div>

                                <p className="fw-semibold mt-4" style={{ fontSize: '1.1rem' }}>
                                    Shopify deletions instantly reflect in your Google Sheet — ensuring both sides stay 100% consistent.
                                </p>
                            </section>

                            <section id="bulk-delete-sheet" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Bulk Delete (Sheet → Shopify)</h3>

                                <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                    When you delete product data directly from your <strong>Google Sheet</strong>,
                                    the products are <strong>deleted from Shopify</strong> —
                                    automatically (if Auto Sync is ON) or when you run sync manually.
                                </p>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>How Deletion from Sheet Works</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        The <strong>Sheet → Shopify Delete Sync</strong> feature ensures that any product you remove from your
                                        Google Sheet is also removed from your Shopify store — automatically and accurately.
                                    </p>

                                    <ol className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-3">
                                            <strong>1. Delete by Clearing Cell Data:</strong>
                                            Sometimes, users select a product row and press the <em>Delete</em> key on their keyboard.
                                            This clears all product information from that row (without removing the row itself).
                                            The system detects this action and deletes the same product from Shopify.
                                        </li>
                                        <li className="mb-3">
                                            <strong>2. Delete by Removing Entire Rows:</strong>
                                            Users can also select one or more product rows and delete them using
                                            <em>Right Click → Delete rows</em> — just like shown in the example image below.
                                            The corresponding products are then automatically deleted from Shopify.
                                        </li>
                                        <li className="mb-3">
                                            <strong>3. Automatic Sync:</strong>
                                            Every deletion made in your sheet is reflected in Shopify, keeping your store data clean.
                                        </li>
                                    </ol>

                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/sheet-delete-syncv3.png"
                                            alt="Bulk delete from Sheet synced to Shopify"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/sheet-delete-syncv3.png')}
                                        />
                                        <p className="mt-3 text-muted text-center" style={{ fontSize: '1rem' }}>
                                            Delete product data in Sheet → Same products deleted in Shopify
                                        </p>
                                    </div>
                                </div>

                                <div className="mb-5 bg-light p-4 rounded border">
                                    <h6 className="fw-bold mb-3" style={{ fontSize: '1.25rem', color: '#212529' }}>Key Notes</h6>
                                    <ul className="list-unstyled mb-0" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-3">• Works with both <strong>cell data clearing</strong> and <strong>row deletion</strong> actions.</li>
                                        <li className="mb-3">• Supports <strong>bulk deletion</strong> of multiple products at once.</li>
                                        <li className="mb-3">• Keeps your <strong>Shopify store and Sheet perfectly in sync</strong> after each delete.</li>
                                        <li className="mb-3">• Ideal for quick cleanup or bulk removal of outdated product data.</li>
                                    </ul>
                                </div>

                                <p className="fw-semibold mt-4" style={{ fontSize: '1.1rem' }}>
                                    Whether you clear cells or delete rows — your Shopify store reflects those changes, maintaining complete data accuracy.
                                </p>
                            </section>

                            <section id="all-jobs" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">All Jobs</h3>

                                <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                    When you click <strong>Job History</strong> in the side menu, you open the <strong>real-time job monitoring dashboard</strong>.
                                    Here you can see the <strong>latest 500 background jobs</strong> — total, active, or completed — with live updates, full details, and complete control.
                                </p>

                                <div className="text-center mb-5">
                                    <img
                                        src="/images/job-history-screenshotv3.png"
                                        alt="Job History Dashboard"
                                        className="img-fluid rounded w-100"
                                        style={{
                                            maxHeight: '520px',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                            objectFit: 'contain',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => setLightboxImage('/images/job-history-screenshotv3.png')}
                                    />
                                    <p className="mt-3 text-muted text-center" style={{ fontSize: '1rem' }}>
                                        Real-time Job History dashboard – live counters, job list, and connection status
                                    </p>
                                </div>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Live Job Counters</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Four real-time counters show the current state of all background jobs:
                                    </p>

                                    <ul className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-2">• <strong>Total Jobs</strong> – total number of jobs ever triggered.</li>
                                        <li className="mb-2">• <strong>Completed</strong> – jobs that finished successfully.</li>
                                        <li className="mb-2">• <strong>Skipped</strong> – jobs cancelled by user actions.</li>
                                        <li className="mb-2">• <strong>Failed</strong> – jobs that ended with an error.</li>
                                        <li className="mb-2">• <strong>Active</strong> – jobs currently running or in queue.</li>
                                    </ul>
                                </div>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Job Details</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Each job in the list shows:
                                    </p>

                                    <ul className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-2">• <strong>Job ID</strong> – unique identifier (e.g. <code>#79622</code>).</li>
                                        <li className="mb-2">• <strong>Action</strong> – <code>Update</code>, <code>Create</code>, <code>Sync</code>, <code>Delete</code>,<code>etc</code>.</li>
                                        <li className="mb-2">• <strong>Direction</strong> – <code>Shopify → Sheet</code> or <code>Sheet → Shopify</code>.</li>
                                        <li className="mb-2">• <strong>Details</strong> – number of products processed (e.g. <code>1 Product</code>).</li>
                                        <li className="mb-2">• <strong>Duration</strong> – execution time + wait time (e.g. <code>3.7s | Wait: 32.5s</code>).</li>
                                        <li className="mb-2">• <strong>Timestamp</strong> – when the job was added.</li>
                                        <li className="mb-2">• <strong>Status</strong> – <code>completed</code> badge for finished jobs.</li>
                                    </ul>
                                </div>

                                <div className="mb-5 p-4 bg-light border rounded">
                                    <h6 className="fw-bold mb-3" style={{ fontSize: '1.25rem', color: '#212529' }}>Job ID Behavior</h6>
                                    <p className="mb-3" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Each job is assigned a <strong>unique Job ID based on the action type</strong> (Add, Update, Sync, Delete).
                                    </p>
                                    <ul className="list-unstyled mb-3" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-2">• <strong>Add, Update, Sync, and Delete</strong> jobs each maintain their own Job ID sequence.</li>
                                        <li className="mb-2">• The same numeric Job ID value can exist across different action types, but it always represents a <strong>different job category</strong>.</li>
                                    </ul>
                                    <div className="bg-white p-3 rounded border mb-3" style={{ fontSize: '1rem' }}>
                                        <strong>Example:</strong><br />
                                        • A <strong>Product Add</strong> job may have Job ID <code>#101</code>.<br />
                                        • A <strong>Product Update</strong> job may also have Job ID <code>#101</code>.<br />
                                        These are treated as <strong>separate jobs</strong> because they belong to different actions.
                                    </div>
                                    <p className="mb-0" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        This ensures clear separation of job tracking by action and easier auditing of your job history.
                                    </p>
                                </div>

                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Search & Filtering</h5>
                                    <p className="mb-3" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <strong>1. Filter by Job ID:</strong> Use the search box to quickly find any job by its ID.
                                    </p>
                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/job-history-filterv3.png"
                                            alt="job history filter"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/job-history-filterv3.png')}
                                        />
                                    </div>

                                    <p className="mb-3" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <strong>2. Filter by Status:</strong> Click on any status card to filter the job list instantly:
                                    </p>
                                    <ul className="list-unstyled mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-2">• <strong>Total Jobs:</strong> View all jobs (no filter).</li>
                                        <li className="mb-2">• <strong>Completed:</strong> Show only successfully finished jobs.</li>
                                        <li className="mb-2">• <strong>Skipped:</strong> Show jobs that were skipped due to <strong>Disconnect</strong>, <strong>Clear All Jobs</strong>, or <strong>Cancel Sync</strong> actions.</li>
                                        <li className="mb-2">• <strong>Failed:</strong> Show only jobs that encountered errors.</li>
                                        <li className="mb-2">• <strong>Active:</strong> Show jobs currently running or in queue.</li>
                                    </ul>

                                    <div className="row g-4 mb-5">
                                        <div className="col-12">
                                            <div className="p-2 border rounded bg-white h-100 shadow-sm">
                                                <img
                                                    src="/images/job-filter-totalv3.png"
                                                    alt="Total Jobs Filter"
                                                    className="img-fluid rounded clickable-image w-100"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setLightboxImage('/images/job-filter-totalv3.png')}
                                                />
                                                <p className="text-center small text-muted mt-2 mb-0">Total Jobs</p>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="p-2 border rounded bg-white h-100 shadow-sm">
                                                <img
                                                    src="/images/job-filter-completedv3.png"
                                                    alt="Completed Jobs Filter"
                                                    className="img-fluid rounded clickable-image w-100"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setLightboxImage('/images/job-filter-completedv3.png')}
                                                />
                                                <p className="text-center small text-muted mt-2 mb-0">Completed Jobs</p>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="p-2 border rounded bg-white h-100 shadow-sm">
                                                <img
                                                    src="/images/job-filter-skipped.png"
                                                    alt="Skipped Jobs Filter"
                                                    className="img-fluid rounded clickable-image w-100"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setLightboxImage('/images/job-filter-skipped.png')}
                                                />
                                                <p className="text-center small text-muted mt-2 mb-0">Skipped Jobs</p>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="p-2 border rounded bg-white h-100 shadow-sm">
                                                <img
                                                    src="/images/job-filter-failedv3.png"
                                                    alt="Failed Jobs Filter"
                                                    className="img-fluid rounded clickable-image w-100"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setLightboxImage('/images/job-filter-failedv3.png')}
                                                />
                                                <p className="text-center small text-muted mt-2 mb-0">Failed Jobs</p>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="p-2 border rounded bg-white h-100 shadow-sm">
                                                <img
                                                    src="/images/job-filter-activev3.png"
                                                    alt="Active Jobs Filter"
                                                    className="img-fluid rounded clickable-image w-100"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setLightboxImage('/images/job-filter-activev3.png')}
                                                />
                                                <p className="text-center small text-muted mt-2 mb-0">Active Jobs</p>
                                            </div>
                                        </div>
                                    </div>

                                    <p className="mb-3" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        The jobs page displays the <strong>latest 500 jobs</strong>, shown in pages (10 per page). Use pagination controls to navigate through the job history.
                                    </p>
                                </div>

                                <p className="mt-5 text-center">
                                    <span className="badge p-3" style={{ fontSize: '1rem', backgroundColor: '#0d9488' }}>
                                        Full visibility. Real-time sync. Complete control.
                                    </span>
                                </p>
                            </section>

                            <section id="settings" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Settings</h3>

                                <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                    Access and review your app permissions and legal approvals in one place.
                                    Only two essential settings are available in <strong>Shopy Sync</strong>.
                                </p>


                                <div className="text-center mb-5">
                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/settings-api-scopesv2.png"
                                            alt="Settings – Shopify API Access Scopes"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/settings-api-scopesv2.png')}
                                        />
                                        <p className="mt-2 text-muted small text-center">
                                            Shopify API Access Scopes
                                        </p>
                                    </div>

                                    {/* Image 2: Approved Documents */}
                                    <div className="text-center mb-5">
                                        <img
                                            src="/images/settings-approved-docsv2.png"
                                            alt="Settings – Approved Documents"
                                            className="img-fluid rounded w-100"
                                            style={{
                                                maxHeight: '520px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                objectFit: 'contain',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setLightboxImage('/images/settings-approved-docsv2.png')}
                                        />
                                        <p className="mt-2 text-muted small text-center">
                                            Approved Documents
                                        </p>
                                    </div>

                                    <p className="mt-4 text-muted text-center" style={{ fontSize: '1rem' }}>
                                        Settings page showing both <strong>API permissions</strong> and <strong>document approval</strong> status
                                    </p>
                                </div>

                                {/* Shopify API Access Scopes */}
                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Shopify API Access Scopes</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Occasionally, we may need to request new Shopify API permissions to enable updated features, or remove access that’s no longer required.
                                    </p>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Any such permission changes will appear clearly in this Settings section for your review and approval.
                                    </p>

                                </div>

                                {/* Approved Documents */}
                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Approved Documents</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Upon installing the app, you accepted key legal documents including the <strong>Terms & Conditions</strong>, <strong>Privacy Notice</strong>, and <strong>Cookies Policy</strong>.
                                    </p>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        In this Settings section, you can view the exact date and time when these documents were approved.
                                    </p>
                                </div>
                            </section>

                            {/* 9. Queued Jobs */}
                            <section id="queued-jobs" data-section className="mb-5">
                                <h3 className="h4 fw-bold text-teal mb-4">Queued Jobs</h3>

                                <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                    The app does not allow multiple sync operations to run at the same time — this prevents slowdowns due to Shopify API rate limits.
                                    Instead, all jobs are <strong>automatically queued</strong> so you can trigger new syncs without waiting.
                                </p>

                                {/* Why We Use Queues */}
                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>Why We Use Job Queues</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Running multiple syncs simultaneously would hit Shopify’s API rate limits, making <strong>every job slower and unreliable</strong>.
                                    </p>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        With <strong>automatic queuing</strong>, you can:
                                    </p>
                                    <ul className="list-unstyled" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-2">Trigger a new sync <u>immediately</u> — no need to wait.</li>
                                        <li className="mb-2">Let the system run jobs <u>one at a time</u> for maximum speed and stability.</li>
                                        <li className="mb-2">Keep working in the sheet while syncs run in the background.</li>
                                    </ul>
                                </div>

                                {/* What Gets Queued */}
                                <div className="mb-5">
                                    <h5 className="fw-bold text-teal mb-4" style={{ fontSize: '1.35rem' }}>All Sync Actions Are Queued</h5>
                                    <p className="mb-4" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        Every background operation is added to the queue:
                                    </p>
                                </div>

                                {/* Queue Behavior */}
                                <div className="mb-5 p-4 bg-light border rounded">
                                    <h6 className="fw-bold mb-3" style={{ fontSize: '1.25rem', color: '#212529' }}>How the Queue Works</h6>
                                    <ul className="list-unstyled mb-0" style={{ fontSize: '1.1rem', color: '#212529' }}>
                                        <li className="mb-2"><strong>One job runs at a time</strong> – next starts only after current one finishes.</li>
                                        <li className="mb-2"><strong>Real-time status</strong> – see “Waiting”, “Running”, or “Completed” in Job History.</li>
                                        <li className="mb-2"><strong>No action needed</strong> – just edit or sync; the system handles the rest.</li>
                                    </ul>
                                </div>

                                <div className="premium-alert mt-4" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                                    <div className="premium-alert-icon" style={{ background: '#22c55e' }}>
                                        <i className="fas fa-rocket"></i>
                                    </div>
                                    <div className="premium-alert-content">
                                        <div className="premium-alert-title" style={{ color: '#15803d' }}>
                                            Performance Best Practice: Smart Batching
                                        </div>
                                        <div className="premium-alert-body" style={{ color: '#14532d' }}>
                                            <p className="mb-2">
                                                Shopy Sync is powerful and designed to handle thousands of product records gracefully.
                                            </p>
                                            <p className="mb-0">
                                                For the <strong>absolute best performance</strong> and smoothest experience, we recommend processing large datasets in <strong>batches of 200–400 products</strong>.
                                                By syncing in smaller chunks (e.g., 400 rows, then the next 400), you ensure <strong>optimal processing speed</strong> for every single product, creating a more efficient and responsive workflow.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                            </section>

                            {/* Extra space at bottom to allow last section to scroll up and trigger highlight */}
                            <div style={{ height: '60vh', pointerEvents: 'none' }}></div>

                        </div>
                    </div>
                </div>
            </section>



        </>
    );
};

export default HowItWorks;